import { useEffect, useRef, useState } from 'react';
import { Alert, Box as MuiBox, Button, DialogContent, Stack } from '@mui/material';
import {
	Save,
	PrecisionManufacturingOutlined,
	PlayArrow,
	DoneAll,
} from '@mui/icons-material';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from '../../components/MoleDialogShell';
import ConfirmDialog from '../../components/ConfirmDialog';
import ProductionDetail from './ProductionDetail';
import ProductionComponents from './ProductionComponents';
import { useAuth } from '../../hooks/useAuth';
import { useDepotAccess } from '../../hooks/useDepotAccess';
import { toast } from 'react-toastify';
import { ProductionTask, useProduction } from './ProductionContext';
import { useUI } from '../../providers/MoleUIContext';
import { LocalStorageKeys, OperationEnum } from '../../util/constants';
import { useAppSocket } from '../../providers/AppSocketContext';
import { useActiveTaskStatusTotal } from '../../hooks/useActiveTaskStatusTotal';
import { Util } from '../../util/Util';
import {
	mapTasksWithActivation,
	refreshEmptyActiveTaskDate,
} from '../../util/taskActivation';
import { loadPendingTasks, persistPendingTasks, useSkipFirstRun } from '../../util/pendingTaskStorage';
import { v4 } from 'uuid';
import { businessToday } from "../../util/businessClock";

import { addMoney, lineTotal } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onSuccess?: () => void;
	/** Open directly in finalize review for a running batch */
	finalizeMode?: boolean;
}

type ConfirmAction = 'save' | 'start' | 'finalize' | null;

const ProductionModal = ({
	isOpen,
	toggle,
	bundle,
	onSuccess,
	finalizeMode = false,
}: Props) => {
	const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
	const [renderChildren, setRenderChildren] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);
	const [validateComponents, setValidateComponents] = useState(false);

	const { api, session, access } = useAuth();
	const { ensureDepotOperation } = useDepotAccess();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const {
		pendingTasks,
		setPendingTasks,
		setFetchedTask,
		activeTask,
		addNewTask,
		setItems,
		outputTotal,
		inputTotal,
		isLocked,
	} = useProduction();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const prevTaskIdRef = useRef<string | null>(null);
	const skipFirstPersist = useSkipFirstRun();

	useActiveTaskStatusTotal(
		outputTotal,
		activeTask?.components?.length ?? 0,
		Boolean(isOpen && activeTask)
	);

	const status = activeTask?.status || 'local';
	const isFinalized = status === 'FINALIZED' || isLocked;
	const canEdit = !isFinalized;
	const canSaveDraft =
		canEdit &&
		validateDetail &&
		validateComponents &&
		(status === 'local' || status === 'DRAFT' || status === 'RUNNING');
	const canStart =
		canEdit &&
		validateDetail &&
		validateComponents &&
		(status === 'DRAFT' || status === 'local');
	const canFinalize =
		canEdit &&
		validateDetail &&
		validateComponents &&
		(status === 'RUNNING' || status === 'DRAFT' || status === 'local' || finalizeMode);

	const toIsoDate = (value: string | Date | undefined | null) => {
		if (!value) return businessToday();
		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
			return value.slice(0, 10);
		}
		const parsed = new Date(value);
		if (Number.isNaN(parsed.getTime())) {
			return businessToday();
		}
		return Util.formatISODate(parsed);
	};

	const buildPayload = (batch: string) => {
		if (!activeTask) return null;
		return {
			details: {
				id: batch,
				receiver: activeTask.receiver,
				depot: activeTask.depot,
				// Always YYYY-MM-DD — never RFC/GMT strings from API dumps
				prod_date: toIsoDate(activeTask.date),
				ref: activeTask.reference,
				description: activeTask.description,
				bom_id: activeTask.bom_id,
				scale: activeTask.scale || 1,
			},
			components: activeTask.components.map((c, idx) => ({
				item: c.item,
				item_desc: c.item_desc,
				role: c.role,
				planned_qty: c.planned_qty || c.quantity,
				quantity: c.quantity,
				unit: c.unit || '',
				amount: c.amount || 0,
				sort_order: idx,
			})),
		};
	};

	const fetchData = async () => {
		if (!bundle?.batch) return;
		setLoadingMessage('FETCH');
		const res = await api.get(`/productions/${bundle.batch}`);
		if (res?.status === 200) {
			setFetchedTask(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
		}
		setLoadingMessage(null);
	};

	const afterSuccess = (msgKey: string) => {
		toast.success(t(msgKey), { autoClose: 3000 });
		if (bundle) {
			toggle();
			onSuccess?.();
		} else {
			setPendingTasks((prev) =>
				prev.filter((task) => task.id !== activeTask?.id)
			);
			onSuccess?.();
		}
	};

	const handleSaveDraft = async () => {
		if (!activeTask) return;
		if (!Util.checkDating(activeTask.date, access, 'production')) return;
		if (!ensureDepotOperation(OperationEnum.PRODUCTION, activeTask.depot)) return;

		const isNew = status === 'local' || !bundle;
		const batch = isNew ? v4() : activeTask.id;
		const payload = buildPayload(batch);
		if (!payload) return;

		setLoadingMessage(isNew ? 'SAVE' : 'UPDATE');
		const res = isNew
			? await api.post('/productions', payload)
			: await api.put(`/productions/${batch}`, payload);

		if (res && (res.status === 201 || res.status === 204 || res.status === 200)) {
			afterSuccess(
				isNew
					? 'common:toast.success.productionDraft'
					: 'common:toast.success.productionSaved'
			);
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.integrity'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
		setConfirmAction(null);
	};

	const handleStart = async () => {
		if (!activeTask) return;
		if (!ensureDepotOperation(OperationEnum.PRODUCTION, activeTask.depot)) return;
		// Ensure draft is persisted first
		let batch = activeTask.id;
		if (status === 'local' || !bundle) {
			if (!Util.checkDating(activeTask.date, access, 'production')) return;
			batch = v4();
			const payload = buildPayload(batch);
			if (!payload) return;
			setLoadingMessage('SAVE');
			const createRes = await api.post('/productions', payload);
			if (!createRes || createRes.status !== 201) {
				setLoadingMessage(null);
				setConfirmAction(null);
				return;
			}
		} else {
			const payload = buildPayload(batch);
			if (payload) {
				setLoadingMessage('UPDATE');
				const saveRes = await api.put(`/productions/${batch}`, payload);
				// Starting after a failed save would run the server's stale lines.
				if (!saveRes || (saveRes.status !== 204 && saveRes.status !== 200)) {
					setLoadingMessage(null);
					setConfirmAction(null);
					return;
				}
			}
		}

		setLoadingMessage('SAVE');
		const res = await api.post(`/productions/${batch}/start`);
		if (res?.status === 200) {
			afterSuccess('common:toast.success.productionStarted');
		}
		setLoadingMessage(null);
		setConfirmAction(null);
	};

	const handleFinalize = async () => {
		if (!activeTask) return;
		if (!Util.checkDating(activeTask.date, access, 'production')) return;
		if (!ensureDepotOperation(OperationEnum.PRODUCTION, activeTask.depot)) return;

		let batch = activeTask.id;
		// Must exist on server
		if (status === 'local' || !bundle) {
			batch = v4();
			const payload = buildPayload(batch);
			if (!payload) return;
			setLoadingMessage('SAVE');
			const createRes = await api.post('/productions', payload);
			if (!createRes || createRes.status !== 201) {
				setLoadingMessage(null);
				setConfirmAction(null);
				return;
			}
			// auto-start then finalize
			await api.post(`/productions/${batch}/start`);
		} else if (status === 'DRAFT') {
			const payload = buildPayload(batch);
			if (payload) {
				setLoadingMessage('UPDATE');
				await api.put(`/productions/${batch}`, payload);
			}
			await api.post(`/productions/${batch}/start`);
		} else if (status === 'RUNNING') {
			const payload = buildPayload(batch);
			if (payload) {
				setLoadingMessage('UPDATE');
				await api.put(`/productions/${batch}`, payload);
			}
		}

		setLoadingMessage('SAVE');
		const payload = buildPayload(batch);
		const res = await api.post(`/productions/${batch}/finalize`, payload || {});
		if (res?.status === 200) {
			afterSuccess('common:toast.success.productionFinalized');
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.integrity'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
		setConfirmAction(null);
	};

	const onConfirm = () => {
		if (confirmAction === 'save') return handleSaveDraft();
		if (confirmAction === 'start') return handleStart();
		if (confirmAction === 'finalize') return handleFinalize();
	};

	useEffect(() => {
		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsed = loadPendingTasks(LocalStorageKeys.PRODUCTION_TASKS, (task: ProductionTask) =>
					refreshEmptyActiveTaskDate({
						...task,
						status: task.status || 'local',
						createdAt: new Date(task.createdAt),
						updatedAt: new Date(task.updatedAt),
						components: task.components || [],
						total: function () {
							return (this.components || [])
								.filter((c: any) => c.role === 'OUTPUT')
								.reduce(
									(sum: number, c: any) =>
										addMoney(sum, lineTotal(c.quantity, c.amount)),
									0
								);
						},
					}), session?.user_id);
				if (parsed) {
					setPendingTasks(parsed);
					return;
				}
				addNewTask();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		// Edit and finalize modals are both mounted and share one ProductionContext,
		// so the closed instance must not touch the task list.
		if (skipFirstPersist()) return;
		if (!isOpen) return;
		if (!bundle) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(
				LocalStorageKeys.PRODUCTION_TASKS,
				session?.user_id,
				JSON.stringify(pendingTasks)
			);
		}
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prev) =>
				mapTasksWithActivation(prev, (_task, index) => index === 0)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}
		} else {
			prevTaskIdRef.current = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
	};

	const title =
		status === 'FINALIZED'
			? t('common:production.viewFinalized')
			: finalizeMode || status === 'RUNNING'
				? t('common:production.finalizeTitle')
				: bundle
					? t('common:ui.labels.amendProduction')
					: t('common:ui.labels.newProduction');

	const confirmTitle =
		confirmAction === 'finalize'
			? t('common:dialog.productionConfirmation')
			: confirmAction === 'start'
				? t('common:production.startConfirmTitle')
				: t('common:production.saveDraftConfirmTitle');

	const confirmMessage =
		confirmAction === 'finalize'
			? t('common:production.finalizeConfirmMessage')
			: confirmAction === 'start'
				? t('common:production.startConfirmMessage')
				: t('common:production.saveDraftConfirmMessage');

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxHeight: '95vh' }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={title}
					subtitle={activeTask?.bom_name || activeTask?.description}
					icon={<PrecisionManufacturingOutlined />}
					onClose={toggle}
					themeColor={themeColor}
				/>
				<DialogContent dividers>
					{renderChildren && activeTask && (
						<>
							{(status === 'RUNNING' || finalizeMode) && (
								<Alert severity="info" sx={{ mb: 2 }}>
									{t('common:production.finalizeHint')}
								</Alert>
							)}
							{status === 'local' || status === 'DRAFT' ? (
								<Alert severity="info" sx={{ mb: 2 }}>
									{t('common:production.draftHint')}
								</Alert>
							) : null}
							<ProductionDetail setValidateCheck={setValidateDetail} />
							<ProductionComponents
								setValidateCheck={setValidateComponents}
								mode={
									status === 'RUNNING' || finalizeMode ? 'finalize' : 'plan'
								}
							/>
							<MuiBox sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
								<strong>
									{t('common:production.inputCost')}:{' '}
									{Util.formatNumber(inputTotal)}
								</strong>
								<strong>
									{t('common:production.outputValue')}:{' '}
									{Util.formatNumber(outputTotal)}
								</strong>
							</MuiBox>
						</>
					)}
				</DialogContent>
				<MoleDialogActions>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
						{canSaveDraft && (
							<Button
								variant="outlined"
								startIcon={<Save />}
								onClick={() => setConfirmAction('save')}
							>
								{t('common:production.saveDraft')}
							</Button>
						)}
						{canStart && (
							<Button
								variant="contained"
								color="warning"
								startIcon={<PlayArrow />}
								onClick={() => setConfirmAction('start')}
							>
								{t('common:production.startRun')}
							</Button>
						)}
						{canFinalize && (
							<Button
								variant="contained"
								color="success"
								startIcon={<DoneAll />}
								onClick={() => setConfirmAction('finalize')}
							>
								{t('common:production.finalize')}
							</Button>
						)}
						<MoleCancelButton onClick={toggle}>
							{t('common:ui.buttons.cancel')}
						</MoleCancelButton>
					</Stack>
				</MoleDialogActions>
			</MoleDialog>

			<ConfirmDialog
				isOpen={!!confirmAction}
				setIsOpen={(open) => {
					if (!open) setConfirmAction(null);
				}}
				title={confirmTitle}
				message={confirmMessage}
				onConfirm={onConfirm}
				confirmBtnColor={confirmAction === 'finalize' ? 'success' : 'primary'}
			/>
		</>
	);
};

export default ProductionModal;
